import { useCallback, useEffect, useState } from 'react'
import { safeRead, safeWrite } from './safeStorage'

// Per-admin UI preferences for sidebar styling. Lives in localStorage
// keyed by user id so two admins on the same browser don't stomp on
// each other. Cross-component sync follows the same pattern as
// useCallBoost: an in-tab CustomEvent carries the new value as detail
// (so private-mode silent-write doesn't make us re-read a stale default
// and revert the just-set value), plus a 'storage' event for cross-tab.

export type SidebarStyle = 'classic' | 'dock'
export type DesktopPosition = 'left' | 'right' | 'top' | 'bottom'
export type MobilePosition = 'top' | 'bottom'

export interface SidebarPrefs {
  style: SidebarStyle
  desktopPosition: DesktopPosition
  mobilePosition: MobilePosition
}

// Dock is the default for first-time admins so they see the new style on
// next login without having to discover the Settings page. Anyone who
// dislikes it can switch back to classic in Settings.
export const DEFAULT_PREFS: SidebarPrefs = {
  style: 'dock',
  desktopPosition: 'left',
  mobilePosition: 'bottom',
}

const PREFS_CHANGED_EVENT = 'pixellab.sidebar.changed'
const KEY_PREFIX = 'pixellab.sidebar.'

interface PrefsChangeDetail {
  userId: string
  prefs: SidebarPrefs
}

function storageKey(userId: string): string {
  return `${KEY_PREFIX}${userId}`
}

function isValidStyle(v: unknown): v is SidebarStyle {
  return v === 'classic' || v === 'dock'
}

function isValidDesktopPosition(v: unknown): v is DesktopPosition {
  return v === 'left' || v === 'right' || v === 'top' || v === 'bottom'
}

function isValidMobilePosition(v: unknown): v is MobilePosition {
  return v === 'top' || v === 'bottom'
}

function parsePrefs(raw: string | null): SidebarPrefs {
  if (!raw) return DEFAULT_PREFS
  try {
    const obj = JSON.parse(raw) as unknown
    if (!obj || typeof obj !== 'object') return DEFAULT_PREFS
    const o = obj as Record<string, unknown>
    return {
      style: isValidStyle(o.style) ? o.style : DEFAULT_PREFS.style,
      desktopPosition: isValidDesktopPosition(o.desktopPosition)
        ? o.desktopPosition
        : DEFAULT_PREFS.desktopPosition,
      mobilePosition: isValidMobilePosition(o.mobilePosition)
        ? o.mobilePosition
        : DEFAULT_PREFS.mobilePosition,
    }
  } catch {
    return DEFAULT_PREFS
  }
}

export function readPrefs(userId: string | undefined): SidebarPrefs {
  if (!userId) return DEFAULT_PREFS
  return parsePrefs(safeRead(storageKey(userId)))
}

export function writePrefs(userId: string, prefs: SidebarPrefs): void {
  safeWrite(storageKey(userId), JSON.stringify(prefs))
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent<PrefsChangeDetail>(PREFS_CHANGED_EVENT, {
        detail: { userId, prefs },
      }),
    )
  }
}

export function useSidebarPrefs(userId: string | undefined): {
  prefs: SidebarPrefs
  setPrefs: (next: SidebarPrefs) => void
} {
  const [prefs, setPrefsState] = useState<SidebarPrefs>(() => readPrefs(userId))
  // Track the userId we last read prefs for. Re-read during render (not in
  // an effect) on transitions — this is React's recommended pattern for
  // state-derived-from-props and avoids the cascading-render lint rule
  // that fires when setState lives in useEffect.
  const [trackedUserId, setTrackedUserId] = useState(userId)
  if (trackedUserId !== userId) {
    setTrackedUserId(userId)
    setPrefsState(userId ? readPrefs(userId) : DEFAULT_PREFS)
  }

  // Subscribe to live updates: same-tab CustomEvent (detail-based, so
  // private-mode silent writes still propagate) and cross-tab `storage`.
  // Filter both by userId so other admins on the same browser don't bleed.
  useEffect(() => {
    if (!userId) return
    const onCustom = (e: Event): void => {
      const detail = (e as CustomEvent<PrefsChangeDetail>).detail
      if (!detail || detail.userId !== userId) return
      setPrefsState(detail.prefs)
    }
    const onStorage = (e: StorageEvent): void => {
      if (e.key !== storageKey(userId)) return
      setPrefsState(parsePrefs(e.newValue))
    }
    window.addEventListener(PREFS_CHANGED_EVENT, onCustom)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(PREFS_CHANGED_EVENT, onCustom)
      window.removeEventListener('storage', onStorage)
    }
  }, [userId])

  const setPrefs = useCallback(
    (next: SidebarPrefs): void => {
      if (!userId) return
      writePrefs(userId, next)
      setPrefsState(next)
    },
    [userId],
  )

  return { prefs, setPrefs }
}
